import { useQuery } from '@tanstack/react-query'
import { CatalogContext } from 'context/CatalogContext'
import { addDays, endOfQuarter, format, previousFriday } from 'date-fns'
import { useContext, useMemo } from 'react'
import { getSpotPrice } from 'services'
import { Source, Ticker } from 'types'

const useQuarterlyListed = ({
  exchange,
  currency,
}: {
  exchange: Source
  currency: Ticker
}) => {
  const catalog = useContext(CatalogContext)
  const { data } = useQuery({
    ...getSpotPrice({ currency }),
  })

  const spot = data || 0
  const delta = Math.floor(spot * 0.05)
  const quarterDate =
    format(new Date(), 'dLLLyy').toUpperCase() ===
    format(endOfQuarter(new Date()), 'dLLLyy').toUpperCase()
      ? format(
          previousFriday(endOfQuarter(addDays(new Date(), 1))),
          'dLLLyy',
        ).toUpperCase()
      : format(previousFriday(endOfQuarter(new Date())), 'dLLLyy').toUpperCase()

  // Find the closest expiry date to our target quarterDate
  const closestExpiryDate = useMemo(() => {
    // Convert the target quarterDate to a Date object for comparison
    const targetDate = new Date(quarterDate)

    // Extract all available expiry dates from the catalog
    const availableExpiries = [
      ...new Set(
        catalog.state.activeOptions
          .filter(
            (item) =>
              item.baseAsset === currency &&
              item.exchange === exchange.toLowerCase() &&
              item.strike,
          )
          .map((item) => item.instrument.split('-')[1]),
      ),
    ]

    // If the exact quarterly date exists, use it
    if (availableExpiries.includes(quarterDate)) {
      return quarterDate
    }

    // Otherwise, find the closest date
    let closestDate = availableExpiries[0]
    let minDiff = Infinity

    availableExpiries.forEach((expiry) => {
      const expiryDate = new Date(expiry)
      const diff = Math.abs(expiryDate.getTime() - targetDate.getTime())

      if (diff < minDiff) {
        minDiff = diff
        closestDate = expiry
      }
    })

    return closestDate
  }, [catalog.state.activeOptions, currency, exchange, quarterDate])

  const listOfActiveExpires = useMemo(() => {
    return catalog.state.activeOptions
      .filter(
        (item) =>
          item.baseAsset === currency &&
          item.exchange === exchange.toLowerCase() &&
          item.instrument.includes(closestExpiryDate) && // Use closestExpiryDate instead of quarterDate
          item.strike &&
          item.strike - spot <= delta &&
          item.strike - spot >= -delta,
      )
      .sort(
        (a, b) =>
          new Date(a.instrument.split('-')[1]).getTime() -
          new Date(b.instrument.split('-')[1]).getTime(),
      )
  }, [
    catalog.state.activeOptions,
    closestExpiryDate,
    spot,
    currency,
    exchange,
    delta,
  ])

  return listOfActiveExpires.slice(0, 8)
}

export default useQuarterlyListed
